import { useCallback, useEffect, useId, useState } from 'react';
import type { RaeditorStore } from '@reyka/openpolotno';
import {
  apiBase,
  withApiKey,
  buildPresetFromElements,
  captureSelectionPreview,
  getSelectedElements,
  type CanvasElementLike,
} from '../selectionTemplates';

type Category = { id: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onStatus: (message: string) => void;
  store: RaeditorStore;
  /** Full selection (mixed types OK); if omitted, read from store. */
  elements?: CanvasElementLike[];
};

export function SaveElementTemplateModal({ open, onClose, onStatus, store, elements }: Props) {
  const titleId = useId();
  const [title, setTitle] = useState('My element');
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState('uncategorized');
  const [newCatName, setNewCatName] = useState('');
  const [showNewCat, setShowNewCat] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingCats, setLoadingCats] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);

  const loadCategories = useCallback(async () => {
    setLoadingCats(true);
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/element-categories`));
      const data = (await res.json().catch(() => ({}))) as {
        categories?: Category[];
        error?: string;
      };
      if (!res.ok) {
        setError(data.error || `Failed to load categories (${res.status})`);
        setCategories([{ id: 'uncategorized', name: 'Uncategorized' }]);
        return;
      }
      const cats = data.categories || [{ id: 'uncategorized', name: 'Uncategorized' }];
      setCategories(cats);
      setCategoryId((prev) =>
        cats.some((c) => c.id === prev) ? prev : cats[0]?.id || 'uncategorized',
      );
    } catch (err) {
      console.error(err);
      setError('Could not reach /api/element-categories');
      setCategories([{ id: 'uncategorized', name: 'Uncategorized' }]);
    } finally {
      setLoadingCats(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTitle('My element');
    setSaving(false);
    setError(null);
    setShowNewCat(false);
    setNewCatName('');
    const els = elements ?? getSelectedElements(store);
    setCount(els.length);
    void loadCategories();
  }, [open, store, elements, loadCategories]);

  if (!open) return null;

  const createCategoryInline = async (): Promise<string | null> => {
    const name = newCatName.trim();
    if (!name) return null;
    const res = await fetch(withApiKey(`${apiBase()}/api/element-categories`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      category?: Category;
      categories?: Category[];
      error?: string;
      message?: string;
    };
    if (!res.ok) {
      throw new Error(data.message || data.error || `Create category failed (${res.status})`);
    }
    if (data.categories) setCategories(data.categories);
    const id = data.category?.id;
    if (id) {
      setCategoryId(id);
      setShowNewCat(false);
      setNewCatName('');
    }
    return id || null;
  };

  const handleSave = async () => {
    const els = elements ?? getSelectedElements(store);
    if (els.length === 0) {
      setError('Nothing selected');
      onStatus('Save element: select at least one canvas element');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let catId = categoryId;
      if (showNewCat && newCatName.trim()) {
        const created = await createCategoryInline();
        if (created) catId = created;
      }
      onStatus('Saving element template…');
      // Full selection as-is (text + images + shapes + …)
      const preset = buildPresetFromElements(els);
      const previewDataUrl = await captureSelectionPreview(store);
      const res = await fetch(withApiKey(`${apiBase()}/api/element-templates`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim() || 'My element',
          categoryId: catId,
          json: preset,
          previewDataUrl,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        id?: string;
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        const msg = data.message || data.error || String(res.status);
        setError(msg);
        onStatus(`Save element failed: ${msg}`);
        return;
      }
      onStatus(`Element saved “${title.trim() || 'My element'}” (${data.id || 'ok'})`);
      onClose();
    } catch (err) {
      console.error(err);
      const msg = err instanceof Error ? err.message : 'Save failed';
      setError(msg);
      onStatus(`Save element failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">
            Save element
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal__lead">
          Save the full selection ({count} element{count === 1 ? '' : 's'} — text, images, shapes,
          etc.) as a reusable pack under a category. Open <strong>My elements</strong> to insert
          later.
        </p>

        <label className="modal__label" htmlFor="save-el-tmpl-title">
          Title
        </label>
        <input
          id="save-el-tmpl-title"
          className="modal__input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="My element"
          autoFocus
        />

        <label className="modal__label" htmlFor="save-el-tmpl-cat">
          Category
        </label>
        {loadingCats ? (
          <div className="modal__status">Loading categories…</div>
        ) : (
          <select
            id="save-el-tmpl-cat"
            className="modal__input"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setShowNewCat(false);
            }}
            disabled={showNewCat}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        <div className="modal__inline-row">
          <button
            type="button"
            className="btn btn--ghost-dark"
            onClick={() => setShowNewCat((v) => !v)}
          >
            {showNewCat ? 'Cancel new category' : 'New category…'}
          </button>
        </div>

        {showNewCat && (
          <>
            <label className="modal__label" htmlFor="save-el-new-cat">
              New category name
            </label>
            <input
              id="save-el-new-cat"
              className="modal__input"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              placeholder="e.g. Ornaments"
            />
          </>
        )}

        {error && (
          <div className="modal__error" role="alert">
            {error}
          </div>
        )}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost-dark" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--accent"
            disabled={
              saving ||
              count === 0 ||
              !title.trim() ||
              (showNewCat && !newCatName.trim())
            }
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save element'}
          </button>
        </div>
      </div>
    </div>
  );
}
